import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Autocomplete,
  TextField,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  ListSubheader,
  Tab,
  Tabs,
  Skeleton,
  Stack,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { BarChart } from "@mui/x-charts/BarChart";
import LanguageIcon from "@mui/icons-material/Language";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import NotesIcon from "@mui/icons-material/Notes";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import SearchIcon from "@mui/icons-material/Search";
import SecurityIcon from "@mui/icons-material/Security";
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import API from "../../../../api";
import { dataGridStyle, colors } from "../../../../theme";
import { isInternalIp } from "../../../../components/ImportantFunctions";
import AnomalyDetailDrawer from "../../tiresia/behaviors/components/AnomalyDetailDrawer";
import LogSidePanel from "../../../../components/LogSidePanel";
import { IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { buildPcapngMulti, downloadBlob } from "../../../../utils/pcap";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useSettings } from "../../../../contexts/SettingsContext";

dayjs.extend(utc);
dayjs.extend(timezone);

const ALL_SEVERITY_TYPES = [
  { tipo: 0, label: "High", color: "#FEA51D" },
  { tipo: 1, label: "Critical", color: "#931A1D" },
  { tipo: 2, label: "Alert", color: "#D1C167" },
  { tipo: 3, label: "Movements", color: "#459296" },
  { tipo: 4, label: "Threat Intelligence", color: "#C485BF" },
];

const SFP_TYPES = ["LECS-CORE-SFP", "LECS-CORE-4-PRO", "LECS-CORE-SFP-PLUS"];

const ALL_CHART_KEYS = ["critical", "high", "alert", "movements", "threat"];
const CHART_COLORS = { critical: "#931A1D", high: "#FEA51D", alert: "#D1C167", movements: "#459296", threat: "#C485BF" };

const OT_CLASS_CONFIG = {
  CRITICAL: { color: "#db4f4a", bg: "rgba(219,79,74,0.15)" },
  HIGH: { color: "#e2724e", bg: "rgba(226,114,78,0.15)" },
  MEDIUM: { color: "#f0c050", bg: "rgba(240,192,80,0.15)" },
  LOW: { color: "#4cceac", bg: "rgba(76,206,172,0.15)" },
};

const BEHAVIOR_SEVERITY_COLORS = {
  high: "#931A1D",
  medium: "#FEA51D",
  low: "#D1C167",
  info: "#459296",
};

const AGG_SEVERITY_LABELS = { 0: "High", 1: "Critical", 2: "Alert", 3: "Movement" };
const AGG_SEVERITY_COLORS = { 0: "#FEA51D", 1: "#931A1D", 2: "#D1C167", 3: "#459296" };
const TAB_COLORS = ["#459095", "#7C4DFF", "#E67E22", "#db4f4a"];

const MENU_SX = {
  list: {
    sx: {
      maxWidth: 220, width: 220, py: 0,
      backgroundColor: "#393939", color: "#eaeaea",
      "& .MuiMenuItem-root": { py: 1, gap: 1.5 },
      "& .MuiListSubheader-root": { backgroundColor: "#2a2a2a", color: "#eaeaea", lineHeight: "36px" },
    },
  },
};

function ClassificationChip({ value }) {
    const upper = value?.toString().toUpperCase();
    const cfg = OT_CLASS_CONFIG[upper] || { color: "#aaa", bg: "rgba(170,170,170,0.1)", icon: null };
    return (
        <Chip
            label={upper || "—"}
            size="small"
            icon={cfg.icon}
            sx={{
                color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg,
                fontWeight: 700, fontSize: "0.7rem",
                "& .MuiChip-icon": { color: cfg.color },
            }}
            variant="outlined"
        />
    );
}

// ── Shared tab label ────────────────────────────────────────────
const TabLabel = ({ icon, label, count, color }) => (
  <Box display="flex" alignItems="center" gap={0.75}>
    {icon}
    <span>{label}</span>
    <Box sx={{
      ml: 0.25, px: 0.75, py: 0.1,
      borderRadius: "10px",
      backgroundColor: `${color}22`,
      border: `1px solid ${color}55`,
      color, fontSize: "0.65rem", fontWeight: 700,
      lineHeight: 1.6, minWidth: 22, textAlign: "center",
    }}>
      {typeof count === "number" ? count?.toLocaleString() : count}
    </Box>
  </Box>
);

// ── Column header row ────────────────────────────────────────────
const ColHeaders = ({ cols }) => (
  <Box sx={{
    display: "grid",
    gridTemplateColumns: cols.map((c) => c.width).join(" "),
    px: 2, py: 0.5,
    borderBottom: "1px solid #222",
    backgroundColor: "#161616",
  }}>
    {cols.map(({ label, align }, i) => (
      <Typography key={i} sx={{
        fontSize: "0.58rem", color: "#666",
        textTransform: "uppercase", letterSpacing: "0.08em",
        textAlign: align ?? "left",
      }}>
        {label}
      </Typography>
    ))}
  </Box>
);

// ── Empty state ─────────────────────────────────────────────────
const EmptyState = ({ message }) => (
  <Box sx={{ py: 4, textAlign: "center" }}>
    <Typography sx={{ fontSize: "0.82rem", color: "#555" }}>{message}</Typography>
  </Box>
);

// ── Loading skeleton for the Behaviors/Cases row lists ───────────
const TabListSkeleton = () => (
  <Stack spacing={0.8} sx={{ p: 2 }}>
    {[...Array(4)].map((_, i) => (
      <Skeleton key={i} variant="rounded" height={44} sx={{ bgcolor: "rgba(255,255,255,0.07)", borderRadius: "12px" }} />
    ))}
  </Stack>
);

const WINDOW_PRESETS = [
  { label: "±10m", minutes: 10 },
  { label: "±30m", minutes: 30 },
  { label: "±1H", minutes: 60 },
  { label: "±6H", minutes: 360 },
  { label: "±12H", minutes: 720 },
  { label: "±24H", minutes: 1440 },
  { label: "±72H", minutes: 4320 },
];

// ══════════════════════════════════════════════════════════════════
const ContextLogTab = ({ prefillIp, prefillDateFrom, prefillDateTo, onPrefillConsumed, otEnabled = true }) => {
  const tz = sessionStorage.getItem("timezone") || "Europe/Amsterdam";
  const isSfp = SFP_TYPES.includes(sessionStorage.getItem("tipoLecs"));
  const SEVERITY_TYPES = isSfp ? ALL_SEVERITY_TYPES.filter((s) => s.tipo !== 3) : ALL_SEVERITY_TYPES;
  const activeChartKeys = isSfp ? ALL_CHART_KEYS.filter((k) => k !== "movements") : ALL_CHART_KEYS;

  const { settings, updateSettings } = useSettings();

  const [ipList, setIpList] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedIp, setSelectedIp] = useState(null);
  const [eventTime, setEventTime] = useState(dayjs().tz(tz));
  const [windowMinutes, setWindowMinutes] = useState(10);
  const [logs, setLogs] = useState([]);
  const [logPanelRow, setLogPanelRow] = useState(null);
  const [behaviors, setBehaviors] = useState([]);
  const [aggregations, setAggregations] = useState([]);
  const [otSecEvents, setOtSecEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [ipLoading, setIpLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [drawerAnomaly, setDrawerAnomaly] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [shouldAutoSearch, setShouldAutoSearch] = useState(false);
  const [hideAcked, setHideAcked] = useState(false);
  const [hideAckedOt, setHideAckedOt] = useState(false);
  const [colVisSecEvents, setColVisSecEvents] = useState(
    settings?.colVis?.["incident-security"] ?? {}
  );
  const [colVisOtSec, setColVisOtSec] = useState(
    settings?.colVis?.["incident-ot"] ?? {}
  );

  useEffect(() => {
    if (settings?.colVis?.["incident-security"]) setColVisSecEvents(settings.colVis["incident-security"]);
    if (settings?.colVis?.["incident-ot"]) setColVisOtSec(settings.colVis["incident-ot"]);
  }, [settings]);

  const handleColVisSecEvents = useCallback((model) => {
    setColVisSecEvents(model);
    updateSettings("colVis", { ...settings?.colVis, "incident-security": model });
  }, [settings, updateSettings]);

  const handleColVisOtSec = useCallback((model) => {
    setColVisOtSec(model);
    updateSettings("colVis", { ...settings?.colVis, "incident-ot": model });
  }, [settings, updateSettings]);

  useEffect(() => {
    if (prefillIp) {
      setSelectedIp(prefillIp);
      setInputValue(prefillIp);
      // Use the incident map's date range when provided
      if (prefillDateFrom && prefillDateTo) {
        const from = dayjs(prefillDateFrom);
        const to = dayjs(prefillDateTo);
        const fullRangeMin = Math.ceil(to.diff(from, "minute", true));
        // Pick the smallest preset whose window reaches at least dateTo from dateFrom
        const matched = WINDOW_PRESETS.find((p) => p.minutes >= fullRangeMin)
          ?? WINDOW_PRESETS[WINDOW_PRESETS.length - 1];
        setEventTime(from.tz(tz));
        setWindowMinutes(matched.minutes);
      } else {
        // Default to ±12H when opening from incident map without explicit range
        setWindowMinutes(720);
        setEventTime(dayjs().tz(tz));
      }
      setShouldAutoSearch(true);
      onPrefillConsumed?.();
    }
  }, [prefillIp, prefillDateFrom, prefillDateTo, onPrefillConsumed, tz]);

  // Auto-trigger search after prefill state has settled
  useEffect(() => {
    if (shouldAutoSearch && selectedIp && eventTime) {
      setShouldAutoSearch(false);
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSearch is redefined every render (not memoized); adding it would re-run this effect on every render instead of only when the auto-search trigger fields change
  }, [shouldAutoSearch, selectedIp, eventTime, windowMinutes]);

  useEffect(() => {
    setIpLoading(true);
    const abortCtrl = new AbortController();
    API.getIpList({ ip: inputValue }, abortCtrl.signal)
      .then((res) => { setIpList(res.data || []); setIpLoading(false); })
      .catch(() => setIpLoading(false));
    return () => abortCtrl.abort();
  }, [inputValue]);

  const getIpString = (val) => {
    if (!val) return null;
    return typeof val === "string" ? val : val.ip;
  };

  // Traccia la ricerca in corso: se ne viene lanciata una nuova prima che
  // questa risponda, va scartata — altrimenti una risposta lenta arrivata
  // in ritardo può sovrascrivere i risultati dell'IP/orario più recente.
  const activeSearchRef = useRef(null);

  useEffect(() => {
    return () => activeSearchRef.current?.abort();
  }, []);

  const handleSearch = () => {
    const ipStr = getIpString(selectedIp) || inputValue.trim();
    if (!ipStr || !eventTime) return;

    activeSearchRef.current?.abort();
    const abortCtrl = new AbortController();
    activeSearchRef.current = abortCtrl;

    setLoading(true);
    setActiveFilters([]);
    setLogs([]);
    setBehaviors([]);
    setAggregations([]);
    setOtSecEvents([]);
    setActiveTab(0);

    const fromTime = eventTime.subtract(windowMinutes, "minute");
    const toTime = eventTime.add(windowMinutes, "minute");
    const timeFilter = [
      fromTime.utc().format("YYYY-MM-DD HH:mm"),
      toTime.utc().format("YYYY-MM-DD HH:mm"),
    ];
    const basePayload = {
      timeFilter, timezone: tz,
      pageNumber: 0, limit: 500,
      hideSuppressed: false, hideBackup: false, group: true,
      filter: [
        { field: "ipSource", operator: "equals", value: ipStr },
        { logic: "or", field: "ipDestination", operator: "equals", value: ipStr },
      ],
    };
    const hoursWindow = Math.max(1, Math.ceil(dayjs().diff(fromTime, "hour", true)));

    Promise.all([
      ...SEVERITY_TYPES.map(({ tipo }) =>
        API.getSecurityLog({ ...basePayload, tipo }, abortCtrl.signal)
          .catch(() => ({ data: { data: [] } }))
      ),
      API.getTiresiaAnomalies({ ips: ipStr, hours: hoursWindow }, abortCtrl.signal)
        .catch(() => ({ data: { data: [] } })),
      API.getAggregations({
        aggregation_dimension: "host",
        timestamp: { dateFrom: fromTime.toISOString(), dateTo: toTime.toISOString() },
      }).catch(() => ({ data: [] })),
      otEnabled
        ? API.getOtSecurityEvents({
            dateFrom: fromTime.toISOString(),
            dateTo: toTime.toISOString(),
            any_ip: ipStr,
            pageSize: 500,
            page: 0,
          }, abortCtrl.signal).catch(() => ({ data: { rows: [] } }))
        : Promise.resolve({ data: { rows: [] } }),
    ]).then((responses) => {
      // Una ricerca più recente ha già preso il posto di questa: scarta.
      if (abortCtrl.signal.aborted) return;

      const behaviorsRes = responses[SEVERITY_TYPES.length];
      const aggRes = responses[SEVERITY_TYPES.length + 1];
      const otSecRes = responses[SEVERITY_TYPES.length + 2];
      const securityResponses = responses.slice(0, SEVERITY_TYPES.length);

      const combined = securityResponses.flatMap((res, i) => {
        const { tipo, label, color } = SEVERITY_TYPES[i];
        return (res.data?.data || []).map((item, idx) => ({
          id: `${tipo}-${idx}`, ...item, tipo,
          severityLabel: label, severityColor: color,
        }));
      });
      setLogs([...combined].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));

      const windowFromMs = fromTime.valueOf();
      const windowToMs = toTime.valueOf();
      setBehaviors(
        (behaviorsRes.data?.data ?? []).filter((a) => {
          if (a.entity?.id !== ipStr) return false;
          const firstRaw = a.first_seen?.$date ?? a.first_seen;
          const lastRaw = a.last_seen?.$date ?? a.last_seen;
          if (!firstRaw && !lastRaw) return true;
          const firstMs = firstRaw ? new Date(firstRaw).getTime() : null;
          const lastMs = lastRaw ? new Date(lastRaw).getTime() : null;
          if (firstMs && lastMs) return firstMs <= windowToMs && lastMs >= windowFromMs;
          if (firstMs) return firstMs <= windowToMs;
          if (lastMs) return lastMs >= windowFromMs;
          return true;
        })
      );
      const aggList = Array.isArray(aggRes.data) ? aggRes.data : (aggRes.data?.data ?? []);
      setAggregations(aggList.filter((c) => (c.host ?? c.group_values?.host) === ipStr));

      const otRows = (otSecRes.data?.rows ?? []).map((r, i) => ({
        ...r,
        id: r._id || r.event_id || `ot-${i}`,
      }));
      setOtSecEvents(otRows);

      setSearched(true);
      setLoading(false);
    });
  };

  const filteredLogs = useMemo(() => {
    if (!activeFilters.length) return logs;
    return logs.filter((log) => activeFilters.every((f) => log[f.field] === f.value));
  }, [logs, activeFilters]);

  const displayLogs = useMemo(() => {
    if (!hideAcked) return filteredLogs;
    return filteredLogs.filter((r) => !r.acked);
  }, [filteredLogs, hideAcked]);

  const displayOtEvents = useMemo(() => {
    if (!hideAckedOt) return otSecEvents;
    return otSecEvents.filter((r) => !r.acked);
  }, [otSecEvents, hideAckedOt]);

  const addFilter = (field, value) => {
    if (!value) { setCtx(null); return; }
    const labels = { src_ip: "Src", dest_ip: "Dst", signature: "Detail" };
    const display = field === "signature"
      ? value.substring(0, 35) + (value.length > 35 ? "…" : "")
      : value;
    setActiveFilters((prev) => {
      if (prev.some((f) => f.field === field && f.value === value)) return prev;
      return [...prev, { field, value, label: `${labels[field]}: ${display}` }];
    });
    setCtx(null);
  };

  const removeFilter = (i) => setActiveFilters((prev) => prev.filter((_, j) => j !== i));

  const chartData = useMemo(() => {
    if (!logs.length) return [];
    const buckets = {};
    logs.forEach((log) => {
      const t = log.timestamp ? new Date(log.timestamp) : null;
      if (!t) return;
      const key = dayjs(t).format("HH:mm");
      if (!buckets[key]) buckets[key] = { time: key, critical: 0, high: 0, alert: 0, movements: 0, threat: 0 };
      if (log.tipo === 1) buckets[key].critical++;
      else if (log.tipo === 0) buckets[key].high++;
      else if (log.tipo === 2) buckets[key].alert++;
      else if (log.tipo === 3) buckets[key].movements++;
      else if (log.tipo === 4) buckets[key].threat++;
    });
    return Object.values(buckets).sort((a, b) => a.time.localeCompare(b.time));
  }, [logs]);

  const anomalyBuckets = useMemo(() => {
    if (!behaviors.length || !chartData.length) return {};
    const date = eventTime.format("YYYY-MM-DD");
    const result = {};
    chartData.forEach(({ time }) => {
      const bucketMs = dayjs(`${date} ${time}`, "YYYY-MM-DD HH:mm").valueOf();
      const active = behaviors.filter((a) => {
        const first = a.first_seen?.$date ?? a.first_seen;
        const last = a.last_seen?.$date ?? a.last_seen;
        if (!first || !last) return false;
        return new Date(first).getTime() <= bucketMs + 60000 && new Date(last).getTime() >= bucketMs;
      });
      if (active.length > 0)
        result[time] = ["high", "medium", "low", "info"].find((s) => active.some((a) => a.severity_label === s)) ?? "info";
    });
    return result;
  }, [behaviors, chartData, eventTime]);

  const columns = [
    {
      field: "__info",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      hideable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="View log details">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setLogPanelRow(params.row); }}
            sx={{ color: colors.grey[100], "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" } }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "timestamp", headerName: "Timestamp", minWidth: 170,
      valueFormatter: (value) => value ? new Date(value || "").toLocaleString() : "-",
    },
    {
      field: "src_ip", headerName: "Source", minWidth: 150,
      renderCell: (params) => <span>{params.value}{params.row.src_port ? `:${params.row.src_port}` : ""}</span>,
    },
    {
      field: "dest_ip", headerName: "Destination", minWidth: 150,
      renderCell: (params) => <span>{params.value}{params.row.dest_port ? `:${params.row.dest_port}` : ""}</span>,
    },
    {
      field: "proto", headerName: "Protocol", minWidth: 90,
      renderCell: (params) => <span>{params.value && params.value !== "failed" ? params.value : "-"}</span>,
    },
    {
      field: "app_proto", headerName: "App Proto", minWidth: 100,
      renderCell: (params) => <span>{params.value && params.value !== "failed" ? params.value : "-"}</span>,
    },
    {
      field: "_scope", headerName: "Scope", minWidth: 100,
      valueGetter: (_v, row) => (!isInternalIp(row.src_ip || "") || !isInternalIp(row.dest_ip || "")) ? "External" : "Internal",
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          {params.value === "External" && (
            <Tooltip title={params.row.countryName || "External"}>
              <LanguageIcon sx={{ fontSize: 14, color: "#C485BF" }} />
            </Tooltip>
          )}
          <span style={{ color: params.value === "External" ? "#C485BF" : "#888", fontSize: "0.8rem" }}>
            {params.value}
          </span>
        </Box>
      ),
    },
    {
      field: "severityLabel", headerName: "Severity", minWidth: 150,
      renderCell: (params) => (
        <Chip label={params.value} size="small"
          sx={{ backgroundColor: params.row.severityColor, color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
      ),
    },
    { field: "signature", headerName: "Details", flex: 2, minWidth: 220 },
  ];

  const displayIp = getIpString(selectedIp) || inputValue.trim();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>

      {/* ── Search bar ────────────────────────────────────────── */}
      <Paper elevation={2} sx={{ p: 2, backgroundColor: "#1e1e1e", border: "1px solid #333" }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <Autocomplete
            freeSolo sx={{ minWidth: 260 }}
            loading={ipLoading} options={ipList}
            inputValue={inputValue}
            onInputChange={(_, v) => { setInputValue(v); setSelectedIp(v); }}
            getOptionLabel={(opt) => typeof opt === "string" ? opt : opt.ip || ""}
            onChange={(_, v) => {
              if (v) { setSelectedIp(v); setInputValue(typeof v === "string" ? v : v.ip); }
            }}
            renderInput={(params) => (
              <TextField {...params} label="IP (source or destination)" size="small"
                placeholder="e.g. 192.168.1.10"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>{ipLoading && <CircularProgress color="inherit" size={16} />}{params.InputProps.endAdornment}</>
                  ),
                }}
              />
            )}
            renderOption={(props, option, { index }) => (
              <li {...props} key={`${typeof option === "string" ? option : option.ip}-${index}`}>
                <span>{typeof option === "string" ? option : option.ip}</span>
                {typeof option !== "string" && option.machine_name && (
                  <span style={{ marginLeft: 8, color: "#FFD700", fontSize: "0.85rem" }}>{option.machine_name}</span>
                )}
                {typeof option !== "string" && option.dns_name && (
                  <span style={{ marginLeft: 8, color: "#4CAF50", fontSize: "0.85rem" }}>{option.dns_name}</span>
                )}
              </li>
            )}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Event time" value={eventTime}
              onChange={(v) => v && setEventTime(v)} ampm={false}
              slotProps={{ textField: { size: "small", sx: { minWidth: 220 } } }}
            />
          </LocalizationProvider>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Time window
            </Typography>
            <ToggleButtonGroup value={windowMinutes} exclusive onChange={(_, v) => v && setWindowMinutes(v)} size="small">
              {WINDOW_PRESETS.map(({ label, minutes }) => (
                <ToggleButton key={minutes} value={minutes} sx={{ px: 1.5, fontSize: "0.8rem" }}>{label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={!inputValue.trim() || loading}
            sx={{ backgroundColor: "#459095", "&:hover": { backgroundColor: "#367a7e" }, height: 36 }}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {/* ── Empty state ───────────────────────────────────────── */}
      {!searched && (
        <Paper elevation={0} sx={{ p: 4, backgroundColor: "#1e1e1e", border: "1px dashed #444", borderRadius: 2, textAlign: "center" }}>
          <Typography color="text.secondary">
            Enter any IP address and an event time, then click <strong>Search</strong> to view context logs.
          </Typography>
        </Paper>
      )}

      {searched && (
        <>
          {/* ── Chart card ───────────────────────────────────── */}
          <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #252535", borderRadius: 1, overflow: "hidden" }}>

            {/* Header */}
            <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", borderBottom: "1px solid #252535" }}>
              <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.9rem", color: "#e8e8e8" }}>
                {displayIp}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#777" }}>
                {eventTime?.format("DD/MM/YYYY HH:mm")} ±{windowMinutes} min
              </Typography>

              {/* Summary badges */}
              <Box display="flex" gap={0.5} ml="auto" alignItems="center" flexWrap="wrap">
                {/* Events severity pills */}
                {activeChartKeys.map((k) => {
                  const cnt = chartData.reduce((s, b) => s + (b[k] ?? 0), 0);
                  if (!cnt) return null;
                  const colors = { critical: "#931A1D", high: "#FEA51D", alert: "#D1C167", movements: "#459296", threat: "#C485BF" };
                  return (
                    <Box key={k} sx={{
                      display: "flex", alignItems: "center", gap: 0.4,
                      px: 0.75, py: 0.2, borderRadius: "4px",
                      backgroundColor: `${colors[k]}18`, border: `1px solid ${colors[k]}44`,
                    }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: "2px", backgroundColor: colors[k], flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: colors[k], fontVariantNumeric: "tabular-nums" }}>
                        {cnt}
                      </Typography>
                    </Box>
                  );
                })}
                {/* Behavior badge */}
                {behaviors.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, px: 0.75, py: 0.2, borderRadius: "4px", backgroundColor: "#7C4DFF15", border: "1px solid #7C4DFF40" }}>
                    <PsychologyAltIcon sx={{ fontSize: 12, color: "#7C4DFF" }} />
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#7C4DFF" }}>{behaviors.length}</Typography>
                  </Box>
                )}
                {/* Cases badge */}
                {aggregations.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, px: 0.75, py: 0.2, borderRadius: "4px", backgroundColor: "#E67E2215", border: "1px solid #E67E2240" }}>
                    <FolderSpecialIcon sx={{ fontSize: 12, color: "#E67E22" }} />
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#E67E22" }}>{aggregations.length}</Typography>
                  </Box>
                )}
                {/* OT Security badge */}
                {otEnabled && otSecEvents.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, px: 0.75, py: 0.2, borderRadius: "4px", backgroundColor: "#db4f4a15", border: "1px solid #db4f4a40" }}>
                    <PrecisionManufacturingIcon sx={{ fontSize: 12, color: "#db4f4a" }} />
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#db4f4a" }}>{otSecEvents.length}</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Chart */}
            {chartData.length > 0 ? (
              <Box sx={{ px: 1 }}>
                <BarChart
                  height={155}
                  dataset={chartData}
                  xAxis={[{ dataKey: "time", scaleType: "band" }]}
                  yAxis={[{ label: "Events" }]}
                  series={activeChartKeys.map((k) => ({
                    dataKey: k,
                    label: ALL_SEVERITY_TYPES.find((s) => s.label.toLowerCase().startsWith(k))?.label ?? k,
                    color: CHART_COLORS[k],
                    stack: "s",
                  }))}
                  margin={{ top: 5, bottom: 20, left: 40, right: 10 }}
                />
                {Object.keys(anomalyBuckets).length > 0 && (
                  <Tooltip title="Colored bars indicate Tiresia anomaly activity in that minute" placement="top">
                    <Box sx={{ display: "flex", pl: "40px", pr: "10px", mb: 1, gap: "2px", cursor: "default" }}>
                      {chartData.map(({ time }) => (
                        <Box key={time} sx={{
                          flex: 1, height: 4, borderRadius: 1,
                          backgroundColor: anomalyBuckets[time]
                            ? `${BEHAVIOR_SEVERITY_COLORS[anomalyBuckets[time]]}bb`
                            : "#1e1e2a",
                          transition: "background-color 0.2s",
                        }} />
                      ))}
                    </Box>
                  </Tooltip>
                )}
              </Box>
            ) : (
              <Box sx={{ px: 2, py: 2.5 }}>
                <Typography color="text.secondary" variant="body2">No events found in this time window.</Typography>
              </Box>
            )}
          </Box>

          {/* ── Tabs card ────────────────────────────────────── */}
          <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #252535", borderRadius: 1, overflow: "hidden" }}>

            {/* Tab bar */}
            <Box sx={{ borderBottom: "1px solid #242424", backgroundColor: "#161616" }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons={false}
                sx={{
                  minHeight: 40,
                  "& .MuiTab-root": {
                    minHeight: 40, py: 0, px: 2,
                    fontSize: "0.72rem", fontWeight: 600,
                    textTransform: "none", color: "#777",
                    "&.Mui-selected": { color: "#e8e8e8" },
                  },
                  "& .MuiTabs-indicator": { height: 2, backgroundColor: TAB_COLORS[activeTab] ?? "#459095" },
                }}
              >
                <Tab label={
                  <TabLabel
                    icon={<SecurityIcon sx={{ fontSize: 13 }} />}
                    label="Security Events"
                    count={displayLogs.length !== logs.length
                      ? `${displayLogs.length}/${logs.length}`
                      : logs.length}
                    color="#459095"
                  />
                } />
                <Tab label={
                  <TabLabel
                    icon={<PsychologyAltIcon sx={{ fontSize: 13 }} />}
                    label="Behaviors"
                    count={behaviors.length}
                    color="#7C4DFF"
                  />
                } />
                <Tab label={
                  <TabLabel
                    icon={<FolderSpecialIcon sx={{ fontSize: 13 }} />}
                    label="Cases"
                    count={aggregations.length}
                    color="#E67E22"
                  />
                } />
                {otEnabled && (
                  <Tab label={
                    <TabLabel
                      icon={<PrecisionManufacturingIcon sx={{ fontSize: 13 }} />}
                      label="OT Security"
                      count={otSecEvents.length}
                      color="#db4f4a"
                    />
                  } />
                )}
              </Tabs>
            </Box>

            {/* ── Tab 0: Security Events ── */}
            {activeTab === 0 && (
              <>
                {/* Toolbar: pcap download of the currently filtered events.
                    Always visible (even when empty) so the user knows where
                    the export lives — the button just disables when there's
                    nothing to bundle. */}
                <Box sx={{
                  px: 2, py: 0.75, display: "flex", gap: 1, alignItems: "center",
                  borderBottom: "1px solid #242424", backgroundColor: "#161616",
                }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setHideAcked(!hideAcked)}
                    startIcon={hideAcked ? <CheckCircleOutlineIcon sx={{ fontSize: 15 }} /> : <HighlightOffIcon sx={{ fontSize: 15 }} />}
                    sx={{
                      fontSize: 11, fontWeight: 600, textTransform: "none",
                      color: hideAcked ? "#4caf50" : "#aaa",
                      borderColor: hideAcked ? "#4caf5055" : "#55555555",
                      "&:hover": { borderColor: "#4caf50", backgroundColor: "#4caf5011" },
                    }}
                  >
                    {hideAcked ? "SHOW ACK'D" : "HIDE ACK'D"}
                  </Button>

                  {(() => {
                    const total = displayLogs?.length || 0;
                    const withCapture = (displayLogs || []).filter(
                      (e) => e?.packet || e?.payload
                    ).length;
                    const disabled = withCapture === 0;
                    const tooltipText =
                      total === 0
                        ? "No events to export."
                        : disabled
                          ? `None of the ${total} filtered events has a stored payload/packet — nothing to bundle.`
                          : `Download a .pcapng of the ${withCapture}/${total} filtered events that have capture data. Each packet carries a Wireshark-visible comment with the detection metadata.`;
                    return (
                      <>
                        <Tooltip arrow title={tooltipText}>
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={disabled}
                              startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
                              onClick={() => {
                                if (disabled) return;
                                const blob = buildPcapngMulti(displayLogs, Date.now());
                                if (!blob) return;
                                const ts = dayjs().format("YYYYMMDD-HHmmss");
                                downloadBlob(blob, `incident-${ts}.pcapng`);
                              }}
                              sx={{
                                minWidth: 0, px: 1.2, py: 0.4,
                                fontSize: 11, fontWeight: 600, textTransform: "none",
                                color: "#459095", borderColor: "#45909555",
                                "&:hover": { borderColor: "#459095", backgroundColor: "#45909522", color: "#5fb0b5" },
                              }}
                            >
                              .pcapng {withCapture > 0 ? `(${withCapture})` : ""}
                            </Button>
                          </span>
                        </Tooltip>
                        <Typography sx={{ fontSize: "0.7rem", color: "#666" }}>
                          {total === 0
                            ? "no events in current filter"
                            : `${withCapture}/${total} events have capture data`}
                        </Typography>
                      </>
                    );
                  })()}
                </Box>

                {activeFilters.length > 0 && (
                  <Box sx={{ px: 2, py: 0.75, display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center", borderBottom: "1px solid #242424", backgroundColor: "#161616" }}>
                    {activeFilters.map((f, i) => (
                      <Chip key={i} label={f.label} size="small" onDelete={() => removeFilter(i)}
                        sx={{ backgroundColor: "#252535", color: "#ccc", fontSize: "0.72rem", height: 20 }} />
                    ))}
                    <Chip label="Clear all" size="small" onClick={() => setActiveFilters([])} variant="outlined"
                      sx={{ fontSize: "0.72rem", color: "#666", borderColor: "#444", height: 20 }} />
                  </Box>
                )}
                <Box sx={{ height: 520, ...dataGridStyle }}>
                  <DataGridPro
                    rows={displayLogs}
                    columns={columns}
                    loading={loading}
                    pageSizeOptions={[50, 100]}
                    initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                    pagination
                    showToolbar
                    columnVisibilityModel={colVisSecEvents}
                    onColumnVisibilityModelChange={handleColVisSecEvents}
                    sx={{ ...dataGridStyle, border: "none", "& .MuiDataGrid-row:hover": { backgroundColor: "#242424" } }}
                    slotProps={{
                      loadingOverlay: { variant: "skeleton" },
                      row: {
                        onContextMenu: (e) => {
                          const id = e.currentTarget.getAttribute("data-id");
                          const row = displayLogs.find((r) => String(r.id) === id);
                          if (!row) return;
                          e.preventDefault();
                          setCtx({ mouseX: e.clientX, mouseY: e.clientY, row });
                        },
                        style: { cursor: "context-menu" },
                      },
                    }}
                  />
                </Box>
              </>
            )}

            {/* ── Tab 1: Behaviors ── */}
            {activeTab === 1 && (
              <Box sx={{ backgroundColor: "#111118" }}>
                {loading ? (
                  <TabListSkeleton />
                ) : behaviors.length === 0 ? (
                  <EmptyState message="No Tiresia behavioral anomalies for this IP as source in the selected time window." />
                ) : (
                  <>
                    <ColHeaders cols={[
                      { label: "Severity", width: "90px" },
                      { label: "Behavior · Peers · Ports", width: "1fr" },
                      { label: "Score", width: "110px", align: "right" },
                    ]} />
                    {behaviors.map((a, i) => {
                      const peers = [
                        ...(a.graph?.edges ?? []).map((e) => e.dst),
                        ...(a.entity?.peers ?? []).map((p) => (typeof p === "string" ? p : p?.dst_ip)),
                      ].filter(Boolean).filter((v, j, arr) => arr.indexOf(v) === j);
                      const ports = [...new Set([
                        ...(a.graph?.edges ?? []).flatMap((e) => e.ports ?? []),
                        ...(a.entity?.ports ?? []),
                      ])].slice(0, 6);
                      return (
                        <Box key={i} onClick={() => setDrawerAnomaly(a)} sx={{
                          display: "grid", gridTemplateColumns: "90px 1fr 110px",
                          alignItems: "start", px: 2, py: 1,
                          cursor: "pointer", borderBottom: "1px solid #1a1a22",
                          "&:hover": { backgroundColor: "#15151f" },
                          "&:last-child": { borderBottom: "none" },
                        }}>
                          <Box pt={0.25}>
                            <Chip label={a.severity_label} size="small" sx={{
                              backgroundColor: BEHAVIOR_SEVERITY_COLORS[a.severity_label] ?? "#555",
                              color: "#fff", fontWeight: 700, fontSize: "0.65rem",
                              height: 19, textTransform: "capitalize",
                            }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.78rem", color: "#d0d0d0", fontWeight: 600, lineHeight: 1.6 }}>
                              {a.dominant_type ?? a.anomaly_types?.[0] ?? "Unknown behavior"}
                              {(a.anomaly_types?.length ?? 0) > 1 && (
                                <Tooltip title={a.anomaly_types.slice(1).join(", ")} arrow>
                                  <Box component="span" sx={{ color: "#7C4DFF", ml: 0.75, fontSize: "0.7rem", cursor: "default" }}>
                                    +{a.anomaly_types.length - 1}
                                  </Box>
                                </Tooltip>
                              )}
                            </Typography>
                            {peers.length > 0 && (
                              <Typography sx={{ fontSize: "0.72rem", color: "#888", lineHeight: 1.5 }}>
                                → {peers.slice(0, 4).join(", ")}
                                {peers.length > 4 && (
                                  <Tooltip title={peers.slice(4).join(", ")} arrow>
                                    <Box component="span" sx={{ color: "#666", ml: 0.5, cursor: "default" }}>
                                      +{peers.length - 4} more
                                    </Box>
                                  </Tooltip>
                                )}
                              </Typography>
                            )}
                            {ports.length > 0 && (
                              <Typography sx={{ fontSize: "0.68rem", color: "#666", lineHeight: 1.5 }}>
                                {ports.join(" · ")}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
                            {a.peak_score != null && (
                              <Typography sx={{ fontSize: "0.75rem", color: "#aaa", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                                {typeof a.peak_score === "number" ? a.peak_score.toFixed(2) : a.peak_score}
                              </Typography>
                            )}
                            {a.occurrence_count != null && (
                              <Typography sx={{ fontSize: "0.68rem", color: "#666", fontVariantNumeric: "tabular-nums" }}>
                                ×{a.occurrence_count?.toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </>
                )}
              </Box>
            )}

            {/* ── Tab 2: Cases ── */}
            {activeTab === 2 && (
              <Box sx={{ backgroundColor: "#120e09" }}>
                {loading ? (
                  <TabListSkeleton />
                ) : aggregations.length === 0 ? (
                  <EmptyState message="No correlation cases for this IP in the selected time window." />
                ) : (
                  <>
                    <ColHeaders cols={[
                      { label: "Severity", width: "100px" },
                      { label: "Rule · Description", width: "1fr" },
                      { label: "Events", width: "80px", align: "right" },
                    ]} />
                    {aggregations.map((c, i) => (
                      <Box key={i} sx={{
                        display: "grid", gridTemplateColumns: "100px 1fr 80px",
                        alignItems: "start", px: 2, py: 1,
                        borderBottom: "1px solid #1e1508",
                        "&:hover": { backgroundColor: "#1a1106" },
                        "&:last-child": { borderBottom: "none" },
                      }}>
                        <Box pt={0.25}>
                          <Chip
                            label={AGG_SEVERITY_LABELS[c.severity] ?? "Unknown"}
                            size="small"
                            sx={{
                              backgroundColor: AGG_SEVERITY_COLORS[c.severity] ?? "#555",
                              color: "#fff", fontWeight: 700, fontSize: "0.65rem",
                              height: 19, textTransform: "capitalize",
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.78rem", color: "#d0d0d0", fontWeight: 600, lineHeight: 1.6 }}>
                            {c.rule_name ?? "Unknown rule"}
                          </Typography>
                          {c.description && (
                            <Typography sx={{ fontSize: "0.72rem", color: "#888", lineHeight: 1.5 }}>
                              {c.description}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 0.25 }}>
                          <Typography sx={{ fontSize: "0.75rem", color: "#aaa", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                            {c.events_count != null ? c.events_count?.toLocaleString() : "—"}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            )}

            {/* ── Tab 3: OT Security ── */}
            {otEnabled && activeTab === 3 && (
              <Box sx={{ backgroundColor: "#0f0a0a" }}>
                {/* OT tab mini-toolbar */}
                <Box sx={{ px: 2, py: 0.75, display: "flex", gap: 1, alignItems: "center", borderBottom: "1px solid #242424", backgroundColor: "#161616" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setHideAckedOt(!hideAckedOt)}
                    startIcon={hideAckedOt ? <CheckCircleOutlineIcon sx={{ fontSize: 15 }} /> : <HighlightOffIcon sx={{ fontSize: 15 }} />}
                    sx={{
                      fontSize: 11, fontWeight: 600, textTransform: "none",
                      color: hideAckedOt ? "#4caf50" : "#aaa",
                      borderColor: hideAckedOt ? "#4caf5055" : "#55555555",
                      "&:hover": { borderColor: "#4caf50", backgroundColor: "#4caf5011" },
                    }}
                  >
                    {hideAckedOt ? "SHOW ACK'D" : "HIDE ACK'D"}
                  </Button>
                </Box>
                {!loading && displayOtEvents.length === 0 ? (
                  <EmptyState message="No OT security events for this IP in the selected time window." />
                ) : (
                  <Box sx={{ height: 480, ...dataGridStyle }}>
                    <DataGridPro
                      rows={displayOtEvents}
                      loading={loading}
                      density="compact"
                      showToolbar
                      pagination
                      pageSizeOptions={[50, 100]}
                      initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                      columnVisibilityModel={colVisOtSec}
                      onColumnVisibilityModelChange={handleColVisOtSec}
                      getRowClassName={({ row }) => {
                        const cls = row.specto_classification;
                        return OT_CLASS_CONFIG[cls] ? `ot-row-${cls.toLowerCase()}` : "";
                      }}
                      sx={{
                        ...dataGridStyle,
                        border: "none",
                        "& .MuiDataGrid-row:hover": { backgroundColor: "#242424" },
                        "& .ot-row-critical": { borderLeft: "3px solid #db4f4a" },
                        "& .ot-row-high": { borderLeft: "3px solid #e2724e" },
                        "& .ot-row-medium": { borderLeft: "3px solid #f0c050" },
                        "& .ot-row-low": { borderLeft: "3px solid #4cceac" },
                      }}
                      slotProps={{ loadingOverlay: { variant: "skeleton" } }}
                      columns={[
                        {
                          field: "timestamp", headerName: "Timestamp", width: 170,
                          valueFormatter: (v) => v ? new Date(v).toLocaleString() : "—",
                        },
                        {
                          field: "severity", headerName: "Severity", width: 145,
                          valueGetter: (value) => value?.toString().toUpperCase() ?? null,
                          renderCell: ({ value }) => <ClassificationChip value={value} />,
                        },
                        { field: "category", headerName: "Classification", width: 180 },
                        {
                          field: "src_ip", headerName: "Source", width: 160,
                          renderCell: ({ value, row }) =>
                            <span>{value}{row.src_port ? `:${row.src_port}` : ""}</span>,
                        },
                        {
                          field: "dest_ip", headerName: "Destination", width: 160,
                          renderCell: ({ value, row }) =>
                            <span>{value}{row.dest_port ? `:${row.dest_port}` : ""}</span>,
                        },
                        { field: "signature", headerName: "Details", flex: 1, minWidth: 200 },
                        { field: "app_proto", headerName: "Protocol", width: 95 },
                      ]}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* ── Right-click context menu ── */}
          <Menu
            open={!!ctx} onClose={() => setCtx(null)}
            anchorReference="anchorPosition"
            anchorPosition={ctx ? { top: ctx.mouseY, left: ctx.mouseX } : undefined}
            slotProps={MENU_SX}
          >
            <ListSubheader>Filter by</ListSubheader>
            <MenuItem onClick={() => addFilter("src_ip", ctx?.row?.src_ip)}>
              <CallMadeIcon fontSize="small" /> Source IP
            </MenuItem>
            <MenuItem onClick={() => addFilter("dest_ip", ctx?.row?.dest_ip)}>
              <CallReceivedIcon fontSize="small" /> Dest IP
            </MenuItem>
            <MenuItem onClick={() => addFilter("signature", ctx?.row?.signature)}>
              <NotesIcon fontSize="small" /> Detail
            </MenuItem>
            {activeFilters.length > 0 && [
              <ListSubheader key="ah">Actions</ListSubheader>,
              <MenuItem key="clear" onClick={() => { setActiveFilters([]); setCtx(null); }}>
                <FilterListOffIcon fontSize="small" /> Clear filters
              </MenuItem>,
            ]}
          </Menu>
        </>
      )}

      <AnomalyDetailDrawer
        anomaly={drawerAnomaly}
        onClose={() => setDrawerAnomaly(null)}
        PORT_TO_PROTOCOL={{}}
      />

      {/* ── Log details side panel ─────────────────────────────────── */}
      <LogSidePanel
        log={logPanelRow}
        open={Boolean(logPanelRow)}
        onClose={() => setLogPanelRow(null)}
      />
    </Box>
  );
};

export default ContextLogTab;
